import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, GeoJSON, Marker, Circle, Popup, useMap, useMapEvents } from 'react-leaflet';
import type { GeoJSON as LeafletGeoJSON, Layer, PathOptions, LatLngBounds, LeafletMouseEvent } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { Check, Eye, Lock, MapPin, Plus, Search, Target, Trash2, X } from 'lucide-react';
import { MapLibreTiles } from '@/lib/maps/MapLibreTiles';
import { countriesQueryOptions, countryLocationsQueryOptions, locationsQueryOptions } from '@/lib/query-options';
import { geocodeAddress, reverseGeocode, fetchLocationBoundary } from '@/lib/apis';
import { formatLocationName } from '@/lib/utils';
import { useAtom } from 'jotai';
import { radiusUnitAtom, radiusInUnit, KM_PER_MILE } from '@/store';
import type { Location, GeocodeResultRead } from '@/lib/types';
import type { RadiusFormValue } from './radius-form';

const DEFAULT_RADIUS_KM = 10;
// #5497: radii are stored in km; the picker displays/enters them in the unit chosen via
// radiusUnitAtom (shared with the radius form + business table). radiusInUnit handles display.
const RADIUS_MIN_KM = 1;
const RADIUS_MAX_KM = 80;
const unitToKm = (val: number, unit: 'km' | 'mi') => (unit === 'mi' ? val * KM_PER_MILE : val);
const clampKm = (km: number) => Math.min(RADIUS_MAX_KM, Math.max(RADIUS_MIN_KM, Math.round(km)));

// Whether the radius popover's button may be pressed. A NEW radius waits for its country:
// the reverse-geocode supplies it, and the business catalog rejects a radius without one.
// A SAVED radius is edited in place and nothing looks its country up again, so holding its
// Save back for one left every caller that passes none (the campaign and Meta Locations
// tabs) on "Resolving location…" for good (#6852).
export const canConfirmRadiusDraft = (draft: { editKey?: string; countryCode: string | null }): boolean =>
  draft.editKey != null || !!draft.countryCode;

// Leaflet's default marker icon URLs break under Vite's asset pipeline; point
// them at the CDN bundle (same approach as the radius MapPreview).
const PIN_ICON = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

/**
 * Map-driven location picker (#5325). Slice 2: countries + per-country regions.
 *
 * - Country polygons from the bundled /geo/countries.geo.json (open data).
 * - "Explore" a country -> its regions come from our own /locations/by-country
 *   (works for every country, addable), with real region POLYGONS lazy-loaded
 *   per country where bundled (Ireland today: /geo/regions/ie.geojson).
 * - Boundary<->geo-target matching is normalized/sorted-token name matching; the
 *   full ISO/geo-target crosswalk is the central #5325 follow-up.
 */

const COUNTRY_STYLE: PathOptions = { color: '#cbd5e1', weight: 1, fillColor: '#e2e8f0', fillOpacity: 0.12 };
const COUNTRY_HOVER: PathOptions = { fillColor: '#290BD9', fillOpacity: 0.28, weight: 1 };
const COUNTRY_SELECTED: PathOptions = { color: '#290BD9', weight: 2, fillColor: '#290BD9', fillOpacity: 0.32 };
// Highlight for the country whose click popover is open (being considered, not yet added).
const COUNTRY_ACTIVE: PathOptions = { color: '#290BD9', weight: 2, fillColor: '#290BD9', fillOpacity: 0.22 };
// Highlight style for a selected geo-target's boundary overlay.
const REGION_SELECTED: PathOptions = { color: '#1d4ed8', weight: 2, fillColor: '#2563eb', fillOpacity: 0.35 };
// Faint outline for the explored country's regions (counties) — visible but clearly
// secondary to a selected/viewed region.
const REGION_OUTLINE: PathOptions = { color: '#2563eb', weight: 1, fillColor: '#2563eb', fillOpacity: 0.06 };
// Dashed frame for the area currently being explored (the context/parent) so you
// keep spatial context even at a leaf you can't drill into further.
const CONTEXT_AREA: PathOptions = { color: '#290BD9', weight: 2, fillColor: '#290BD9', fillOpacity: 0.05, dashArray: '6 4' };
// Exclude-intent styles (#6214): inside the Add Exclusion picker the selection paints RED — the
// user is removing reach, not adding it, and the cobalt fill read as "adding a location" (Jeff).
const EXCLUDE_COUNTRY_HOVER: PathOptions = { fillColor: '#DC2626', fillOpacity: 0.28, weight: 1 };
const EXCLUDE_COUNTRY_SELECTED: PathOptions = { color: '#B91C1C', weight: 2, fillColor: '#DC2626', fillOpacity: 0.32 };
const EXCLUDE_COUNTRY_ACTIVE: PathOptions = { color: '#B91C1C', weight: 2, fillColor: '#DC2626', fillOpacity: 0.22 };
const EXCLUDE_REGION_SELECTED: PathOptions = { color: '#B91C1C', weight: 2, fillColor: '#DC2626', fillOpacity: 0.35 };

function norm(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .trim();
}

// our-name (normalized) -> geojson-name (normalized) for common country mismatches.
const NAME_ALIAS: Record<string, string> = {
  'united states': 'united states of america',
  czechia: 'czech republic',
  serbia: 'republic of serbia',
  'north macedonia': 'macedonia',
  tanzania: 'united republic of tanzania',
  bahamas: 'the bahamas',
};

const isCountry = (loc: Location) => loc.target_type?.toLowerCase() === 'country';
// Explorable = can drill into sub-regions: countries always can; sub-country rows
// carry has_children from both /locations/by-country (browse) and /locations (search),
// so Explore shows consistently however the place was reached (#5511).
const isExplorable = (loc: Location) => isCountry(loc) || loc.has_children === true;

// Order result rows largest geography first (country → region/state → county →
// city → postal/point), then alphabetically within a tier.
const TYPE_RANK: Record<string, number> = {
  country: 0,
  region: 1,
  state: 1,
  province: 1,
  'autonomous community': 1,
  prefecture: 1,
  territory: 1,
  'union territory': 1,
  oblast: 1,
  krai: 1,
  canton: 1,
  governorate: 1,
  department: 1,
  republic: 1,
  'administrative region': 1,
  'autonomous region': 1,
  'federal district': 1,
  county: 2,
  district: 2,
  municipality: 2,
  borough: 2,
  city: 3,
  'colloquial area': 3,
  neighborhood: 4,
  airport: 4,
  university: 4,
  'national park': 4,
  'tv region': 4,
  'postal code': 5,
};
const typeRank = (t?: string): number => TYPE_RANK[(t || '').toLowerCase()] ?? 6;
// Area-type = has a real boundary worth outlining (region/state/county/district),
// vs points like city/airport/postal code. Used to keep the explore map to actual
// borders rather than geocoding thousands of towns.
const isArea = (loc: Location): boolean => typeRank(loc.target_type) <= 2;
const byTypeThenName = (a: Location, b: Location): number =>
  typeRank(a.target_type) - typeRank(b.target_type) ||
  formatLocationName(a.name).localeCompare(formatLocationName(b.name));

// Wrap a bare GeoJSON geometry in a Feature — react-leaflet's <GeoJSON> and
// L.geoJSON are reliable with Features but can choke on a raw geometry.
const featureOf = (geom: Geometry): Feature => ({ type: 'Feature', geometry: geom, properties: {} });

interface LocationMapPickerProps {
  businessId: number;
  selected: Location[];
  onChange: (next: Location[]) => void;
  /** Criteria ids a campaign positively targets — locked from removal (#4816).
   * Their Remove affordance is greyed and remove handlers no-op on them. */
  lockedCriteriaIds?: Set<number>;
  /** #5466: criteria ids already excluded on the campaign. Search results, the region
   * list, and the country popover mark these with an "Excluded" badge so the user sees
   * at a glance that it's already excluded (and that targeting it would conflict). */
  excludedCriteriaIds?: Set<number>;
  radii?: RadiusFormValue[];
  onRadiiChange?: (next: RadiusFormValue[]) => void;
  /** When set, open straight into editing this saved radius (Radius tab + its popover). */
  initialRadiusEdit?: RadiusFormValue | null;
  /** Catalog-only mode (campaign wizard): hide the Radius tab + proximity UI, since
   * campaign targeting uses catalog locations only (no radius/worldwide). */
  catalogOnly?: boolean;
  /** When set (campaign wizard), the "Your locations" list becomes a checklist of
   * these candidates (the business's saved locations) to apply to the campaign. */
  candidates?: Location[];
  /** When set (campaign wizard), shows the Radius tab even in catalogOnly mode and
   * turns "Your radius targets" into a checklist of these candidates (the business's
   * saved radius locations) to apply to the campaign — mirrors `candidates` (#5444). */
  radiusCandidates?: RadiusFormValue[];
  /** Single-select mode (Location-type campaign): radios instead of checkboxes,
   * and adding a location replaces the current one (exactly one target). */
  singleSelect?: boolean;
  /** Exclusion picker (#6214): selections paint RED (error palette) instead of cobalt so adding
   * an exclusion never reads as adding a location. Defaults to the include (cobalt) treatment. */
  intent?: 'include' | 'exclude';
}

export function LocationMapPicker({
  businessId,
  selected,
  onChange,
  lockedCriteriaIds,
  excludedCriteriaIds,
  radii = [],
  onRadiiChange = () => {},
  initialRadiusEdit = null,
  catalogOnly = false,
  candidates,
  radiusCandidates,
  singleSelect = false,
  intent = 'include',
}: LocationMapPickerProps) {
  // The intent-resolved selection styles — every selected/hover surface below uses these, so the
  // whole selection experience flips together (identity comparisons included).
  const countrySelectedStyle = intent === 'exclude' ? EXCLUDE_COUNTRY_SELECTED : COUNTRY_SELECTED;
  const countryHoverStyle = intent === 'exclude' ? EXCLUDE_COUNTRY_HOVER : COUNTRY_HOVER;
  const countryActiveStyle = intent === 'exclude' ? EXCLUDE_COUNTRY_ACTIVE : COUNTRY_ACTIVE;
  const regionSelectedStyle = intent === 'exclude' ? EXCLUDE_REGION_SELECTED : REGION_SELECTED;
  const { data: countries } = useQuery(countriesQueryOptions());
  const [geo, setGeo] = useState<FeatureCollection | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 300);
  // Breadcrumb trail of explored locations (United Kingdom › England › …), shown
  // as pills in the search bar. The deepest entry is the active explore context.
  const [path, setPath] = useState<Location[]>([]);
  const context = path.length > 0 ? path[path.length - 1] : null;
  // Which kind of target the user is adding: catalog geo (Locations) or
  // address radius (Proximity, formerly "radius").
  const [tab, setTab] = useState<'locations' | 'proximity'>(initialRadiusEdit ? 'proximity' : 'locations');
  // Type filter for the always-visible "Your locations" list (empty = show all).
  // Map fly-to target: set when a sidebar item is clicked (#4). New object each
  // click so the effect re-fires even for the same item.
  const [flyTo, setFlyTo] = useState<{ bounds: LatLngBounds } | null>(() =>
    initialRadiusEdit
      ? { bounds: L.latLng(initialRadiusEdit.lat, initialRadiusEdit.lng).toBounds(Math.max(1, initialRadiusEdit.radiusKm) * 2400) }
      : null,
  );

  useEffect(() => {
    let alive = true;
    fetch('/geo/countries.geo.json')
      .then((r) => r.json())
      .then((d: FeatureCollection) => alive && setGeo(d))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // Region list for the explored country (works for any country — from our data).
  // keepPreviousData so filtering doesn't blank the list to "No regions found"
  // between keystrokes; isFetching drives a "Searching…" hint instead.
  const { data: regions, isFetching: regionsFetching } = useQuery({
    ...countryLocationsQueryOptions(
      context ? context.country_code : null,
      context?.criteria_id ?? null,
      context ? debouncedQuery : '',
    ),
    placeholderData: keepPreviousData,
  });

  // Catalog all-type search (city / region / postcode / admin / airport / country)
  // via Google Ads geo-target suggestions — only on the Locations tab.
  const { data: searchResults, isFetching: searchFetching } = useQuery(
    locationsQueryOptions(tab === 'locations' && !context ? debouncedQuery : ''),
  );

  // Address geocoding for proximity targets — only on the Proximity tab.
  const { data: geoResults = [], isFetching: geoFetching } = useQuery({
    queryKey: ['geocode-search', businessId, debouncedQuery],
    queryFn: () => geocodeAddress(businessId, debouncedQuery, 5),
    enabled: tab === 'proximity' && debouncedQuery.trim().length >= 3,
    staleTime: 5 * 60 * 1000,
  });

  // The catalog location currently being previewed (View / Explore / sidebar
  // click). Its boundary highlights even before it's added, alongside the added
  // set — so View and Explore show the area on the map, not just Add.
  const [focused, setFocused] = useState<Location | null>(null);
  // criteria_id whose boundary is being fetched for View — drives the per-button
  // spinner so a first-time View reads as loading, not broken.
  const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
  // The result row the pointer is currently over — its boundary highlights solid
  // on the map so hovering the list previews each county (#5359 follow-up).
  const [hovered, setHovered] = useState<Location | null>(null);

  // Boundary overlays resolved on demand from each geo-target's canonical name
  // (Google Ads geo-targets are the source of truth; the boundary is just the
  // visual). `null` = looked up, no boundary.
  const [boundaries, setBoundaries] = useState<Record<number, Geometry | null>>({});
  // criteria_ids whose fetch has been kicked off (in-flight or done) so each is
  // requested exactly once, and a subsequent add/remove can't cancel an in-flight
  // request — the cause of overlays rendering only "randomly" after an add.
  const requestedRef = useRef<Set<number>>(new Set());
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    // While exploring a country, also resolve its regions so their county outlines
    // draw on the map (#5359). Boundary-on-demand is per-target, so these stream in
    // as the geocoder serves them (cached cluster-wide after the first explore).
    // Only resolve boundaries for the explored level's *area* sub-regions — not its
    // cities/postal codes (England alone has ~4k children, mostly points).
    const exploreRegions = context && tab === 'locations' ? (regions ?? []).filter(isArea) : [];
    const wanted = [
      ...selected,
      ...(focused ? [focused] : []),
      ...(hovered ? [hovered] : []),
      ...(context ? [context] : []), // the explored area's own outline (context frame)
      ...exploreRegions,
    ].filter((l) => !isCountry(l));
    wanted.forEach((loc) => {
      if (requestedRef.current.has(loc.criteria_id)) return;
      requestedRef.current.add(loc.criteria_id);
      fetchLocationBoundary(businessId, loc.canonical_name || loc.name, loc.criteria_id).then((geom) => {
        if (mountedRef.current) setBoundaries((b) => ({ ...b, [loc.criteria_id]: geom }));
      });
    });
  }, [selected, focused, hovered, businessId, regions, context, tab]);

  // Catalog locations whose boundary overlay should render solid: the added set
  // plus the focused and hovered ones, deduped. Countries highlight via the
  // country layer, not here.
  const overlayLocations = useMemo(() => {
    const m = new Map<number, Location>();
    for (const s of selected) if (!isCountry(s)) m.set(s.criteria_id, s);
    if (focused && !isCountry(focused)) m.set(focused.criteria_id, focused);
    if (hovered && !isCountry(hovered)) m.set(hovered.criteria_id, hovered);
    return [...m.values()];
  }, [selected, focused, hovered]);

  // True while any boundary we need (added / focused / the explored area + its
  // regions) is still resolving — drives the map "Locating…" indicator so a
  // first-time View/Explore reads as loading, not broken.
  const resolvingBoundaries = useMemo(() => {
    const pending = [
      ...selected,
      ...(focused ? [focused] : []),
      ...(context ? [context] : []),
      ...(context && tab === 'locations' ? (regions ?? []).filter(isArea) : []),
    ].filter((l) => !isCountry(l));
    return pending.some((l) => !(l.criteria_id in boundaries));
  }, [selected, focused, context, regions, tab, boundaries]);

  // ---- country <-> geojson name maps -------------------------------------
  const { geoNameByCriteria, criteriaByGeoName } = useMemo(() => {
    const byCriteria = new Map<number, string>();
    const byGeoName = new Map<string, number>();
    if (!countries || !geo) return { geoNameByCriteria: byCriteria, criteriaByGeoName: byGeoName };
    const featureByNorm = new Map<string, string>();
    for (const f of geo.features) {
      const nm = (f.properties as { name?: string } | null)?.name;
      if (nm) featureByNorm.set(norm(nm), nm);
    }
    for (const c of countries) {
      const geoName = featureByNorm.get(NAME_ALIAS[norm(c.name)] ?? norm(c.name));
      if (geoName) {
        byCriteria.set(c.criteria_id, geoName);
        byGeoName.set(geoName, c.criteria_id);
      }
    }
    return { geoNameByCriteria: byCriteria, criteriaByGeoName: byGeoName };
  }, [countries, geo]);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.criteria_id)), [selected]);
  const selectedGeoNames = useMemo(() => {
    const set = new Set<string>();
    for (const s of selected) {
      if (isCountry(s)) {
        const g = geoNameByCriteria.get(s.criteria_id);
        if (g) set.add(g);
      }
    }
    return set;
  }, [selected, geoNameByCriteria]);


  // Clicking a country on the map opens a details popover (with Add), rather
  // than auto-adding it.
  const [countryPopup, setCountryPopup] = useState<{ loc: Location; lat: number; lng: number } | null>(null);
  // Proximity tab: clicking the map drops a draft pin, reverse-geocodes it, and
  // offers to add it as a radius (#2).
  const [proximityDraft, setProximityDraft] = useState<{
    lat: number;
    lng: number;
    address: string;
    radiusKm: number;
    // Resolved from the reverse-geocode — the backend requires a country_code on
    // every non-global target, so a radius added without one is rejected (422).
    countryCode: string | null;
    // True while the reverse-geocode is in flight — drives the popover's loading state.
    locating: boolean;
    // Set when editing a saved radius (its "lat:lng" key); save updates in place
    // rather than adding a new one.
    editKey?: string;
  } | null>(() =>
    initialRadiusEdit
      ? {
          lat: initialRadiusEdit.lat,
          lng: initialRadiusEdit.lng,
          address: initialRadiusEdit.formattedAddress,
          radiusKm: initialRadiusEdit.radiusKm,
          countryCode: initialRadiusEdit.countryCode,
          locating: false,
          editKey: `${initialRadiusEdit.lat}:${initialRadiusEdit.lng}`,
        }
      : null,
  );
  // Stable [lat,lng] for the draft Circle/Marker/Popup. Memoised on lat/lng only so a
  // radius-km change doesn't hand react-leaflet a new position array each render —
  // which re-opens the Popup (the whole popover "flashing") on every stepper click (#5434).
  const draftCenter = useMemo<[number, number] | null>(
    () => (proximityDraft ? [proximityDraft.lat, proximityDraft.lng] : null),
    [proximityDraft?.lat, proximityDraft?.lng],
  );
  // True while we geocode a carried-over location (e.g. "Proximity" from a leaf) to
  // seed its radius draft — drives the map "Locating…" indicator.
  const [seedingProximity, setSeedingProximity] = useState(false);
  // #5497: distance unit for the radius control (display + entry); storage stays km.
  // Shared atom so the radius form and the business table show the same unit.
  const [radiusUnit, setRadiusUnit] = useAtom(radiusUnitAtom);
  // #5497: opening straight into editing a saved radius (the table's edit button seeds
  // initialRadiusEdit, bypassing focusRadius) must adopt that radius's unit — otherwise
  // Save would restamp it to whatever the atom happened to be. Mount-only so it doesn't
  // fight a manual toggle mid-edit.
  useEffect(() => {
    if (initialRadiusEdit) setRadiusUnit(initialRadiusEdit.unit ?? 'km');
  }, []);
  const pickProximity = async (lat: number, lng: number) => {
    const fallback = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    setProximityDraft({ lat, lng, address: fallback, radiusKm: DEFAULT_RADIUS_KM, countryCode: null, locating: true });
    // Clear (don't fill) the search: dropping a radius via the map shouldn't rewrite
    // the search bar — the draft popover already shows the address, and a filled query
    // would pop a redundant results dropdown over it (#5435).
    setQuery('');
    try {
      const res = await reverseGeocode(businessId, lat, lng);
      if (res) {
        setProximityDraft((d) =>
          d ? { ...d, address: res.formatted_address, countryCode: res.country_code, locating: false } : d,
        );
      } else {
        setProximityDraft((d) => (d ? { ...d, locating: false } : d));
      }
    } catch {
      setProximityDraft((d) => (d ? { ...d, locating: false } : d)); // keep coords as the label
    }
  };

  // Search results live in a dropdown; each row has View (fly to it) + Add/Remove.
  const addCatalogNow = (loc: Location) => {
    // Single-select (Location campaign): adding replaces the current target.
    if (singleSelect) {
      onChange([loc]);
      return;
    }
    if (!selectedIds.has(loc.criteria_id)) onChange([...selected, loc]);
  };
  // Campaign-locked locations can't be removed here (#4816) — no-op so the X in
  // search results / popups does nothing rather than staging a removal the
  // backend rejects. The "Your locations" list greys the affordance outright.
  const removeCatalog = (criteriaId: number) => {
    if (lockedCriteriaIds?.has(criteriaId)) return;
    onChange(selected.filter((s) => s.criteria_id !== criteriaId));
  };
  const removeCatalogNow = (loc: Location) => removeCatalog(loc.criteria_id);
  // Wizard checklist: pick (single-select) or toggle (multi-select) a candidate.
  const toggleCatalog = (loc: Location) =>
    singleSelect ? onChange([loc]) : selectedIds.has(loc.criteria_id) ? removeCatalogNow(loc) : addCatalogNow(loc);
  // Wizard radius checklist (#5444): check/uncheck a radius candidate, matched by
  // coordinates (radius_km may differ if edited).
  const toggleRadius = (r: RadiusFormValue) => {
    const present = radii.some((x) => x.lat === r.lat && x.lng === r.lng);
    onRadiiChange(present ? radii.filter((x) => !(x.lat === r.lat && x.lng === r.lng)) : [...radii, r]);
  };
  // Wizard checklist (#5444): select-all / clear for the "Apply to this campaign"
  // lists. Select all checks the whole pool (business candidates ∪ campaign-unique);
  // clear unchecks everything.
  const selectAllCatalog = (select: boolean) =>
    onChange(
      select
        ? [...new Map([...(candidates ?? []), ...selected].map((l) => [l.criteria_id, l])).values()]
        : [],
    );
  const selectAllRadii = (select: boolean) =>
    onRadiiChange(
      select
        ? [...new Map([...(radiusCandidates ?? []), ...radii].map((r) => [`${r.lat}:${r.lng}`, r])).values()]
        : [],
    );
  const addAddrNow = (r: GeocodeResultRead) => {
    if (!radii.some((x) => x.lat === r.lat && x.lng === r.lng)) {
      onRadiiChange([
        ...radii,
        {
          lat: r.lat,
          lng: r.lng,
          radiusKm: DEFAULT_RADIUS_KM,
          countryCode: r.country_code,
          formattedAddress: r.formatted_address,
          unit: radiusUnit,
        },
      ]);
    }
  };
  const removeAddrNow = (r: GeocodeResultRead) => {
    onRadiiChange(radii.filter((x) => !(x.lat === r.lat && x.lng === r.lng)));
  };
  // Dismiss the results dropdown (works for both tabs): clear the search and any
  // explore context so the panel closes back to the "Your locations" list.
  const dismissResults = () => {
    setQuery('');
    setPath([]);
  };

  // #5494: a click anywhere outside the results panel and its search bar dismisses it (same as ✕).
  const resultsRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const showing = tab === 'proximity' ? query.trim().length > 0 : path.length > 0 || query.trim().length > 0;
    if (!showing) return;
    const onDown = (e: MouseEvent) => {
      const node = e.target as Node;
      if (resultsRef.current?.contains(node) || searchBarRef.current?.contains(node)) return;
      dismissResults();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tab, query, path]);

  // Suppress country hover only while a radius address is mid-edit (marked but
  // not yet added); hover returns once it's saved.
  const editingRef = useRef(false);
  editingRef.current = proximityDraft !== null;
  // Set true when an in-popover button (Add radius / Save) closes the draft, so the
  // SAME click can't leak through to the now-active map handler and drop a phantom
  // pin. MapClickHandler consumes it on the next click (#5435).
  const suppressMapClickRef = useRef(false);
  // Country click only adds a catalog target on the Locations tab; on Proximity
  // the map click drops a pin instead (read via ref so the layer handler is live).
  const tabRef = useRef(tab);
  tabRef.current = tab;
  // geojson name of the country currently being explored — its base-layer hover is
  // suppressed so the purple fill doesn't swallow the county outlines (#5359).
  const exploredCountryGeoNameRef = useRef<string | null>(null);

  // ---- country geojson layer (imperative styling, 180 features) -----------
  // Assigned after focusLocation/exploreLocation are defined so the click handler
  // (bound once per feature) can zoom + open the region popover.
  const openCountryPopupRef = useRef<(geoName: string, lat: number, lng: number) => void>(() => {});
  // geojson name of the country whose click popover is open — highlighted so the
  // clicked country stays visibly selected while its popover (Explore / Add) shows.
  const popoverCountryGeoName =
    countryPopup && isCountry(countryPopup.loc) ? (geoNameByCriteria.get(countryPopup.loc.criteria_id) ?? null) : null;

  // Resting style for a country layer by name: added countries read as selected,
  // the popover's country as active, everything else plain. A ref so the hover
  // handlers (bound once per feature in onEachCountry) always see the live state.
  const restingStyleRef = useRef<(nm: string) => PathOptions>(() => COUNTRY_STYLE);
  // On the Radius tab the map is only for dropping pins — the selected/active country fills
  // (purple) shouldn't show, so everything rests plain there (#5495). And the country you've
  // explored into rests plain too — you've moved to a deeper layer, so its fill goes (#5510).
  restingStyleRef.current = (nm: string) =>
    tab === 'proximity' || nm === exploredCountryGeoNameRef.current
      ? COUNTRY_STYLE
      : selectedGeoNames.has(nm)
        ? countrySelectedStyle
        : nm === popoverCountryGeoName
          ? countryActiveStyle
          : COUNTRY_STYLE;
  const geoLayerRef = useRef<LeafletGeoJSON | null>(null);
  useEffect(() => {
    geoLayerRef.current?.eachLayer((layer: Layer) => {
      const nm = ((layer as Layer & { feature?: Feature }).feature?.properties as { name?: string } | undefined)?.name;
      if (nm) (layer as Layer & { setStyle?: (s: PathOptions) => void }).setStyle?.(restingStyleRef.current(nm));
    });
  }, [selectedGeoNames, popoverCountryGeoName, geo, tab, context]);
  const onEachCountry = (feature: Feature, layer: Layer) => {
    const nm = (feature.properties as { name?: string } | null)?.name ?? '';
    const setStyle = (layer as Layer & { setStyle?: (s: PathOptions) => void }).setStyle?.bind(layer);
    layer.on('click', (e: LeafletMouseEvent) => {
      if (tabRef.current === 'locations') openCountryPopupRef.current(nm, e.latlng.lat, e.latlng.lng);
    });
    // Hover-highlight every country except the added one (fixed selected style)
    // and the one being explored (its purple fill would bury the county outlines);
    // on mouseout snap back to the resting style.
    layer.on('mouseover', () => {
      if (
        // Locations-only: on the Radius tab the map is for dropping pins, so the
        // country hover-highlight shouldn't fire (#5436).
        tabRef.current === 'locations' &&
        !editingRef.current &&
        restingStyleRef.current(nm) !== countrySelectedStyle &&
        nm !== exploredCountryGeoNameRef.current
      )
        setStyle?.(countryHoverStyle);
    });
    layer.on('mouseout', () => setStyle?.(restingStyleRef.current(nm)));
  };

  // country_code -> geojson country name, for framing a region by its country.
  const countryGeoNameByCC = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of countries ?? []) {
      const gn = geoNameByCriteria.get(c.criteria_id);
      if (gn && c.country_code) m.set(c.country_code.toUpperCase(), gn);
    }
    return m;
  }, [countries, geoNameByCriteria]);

  // The country containing the current explored context (the country itself, or a
  // sub-region's country). Kept in a ref so the bound hover handler stays live.
  const exploredCountryGeoName = context
    ? (isCountry(context)
        ? (geoNameByCriteria.get(context.criteria_id) ?? null)
        : (countryGeoNameByCC.get(context.country_code?.toUpperCase() ?? '') ?? null))
    : null;
  exploredCountryGeoNameRef.current = exploredCountryGeoName;

  const boundsForGeoName = (geoName?: string): LatLngBounds | null => {
    if (!geoName) return null;
    let b: LatLngBounds | null = null;
    geoLayerRef.current?.eachLayer((layer: Layer) => {
      const nm = ((layer as Layer & { feature?: Feature }).feature?.properties as { name?: string } | undefined)?.name;
      if (nm && nm === geoName) b = (layer as Layer & { getBounds?: () => LatLngBounds }).getBounds?.() ?? null;
    });
    return b;
  };
  // #5500: no auto-zoom. The map opens at its default view and only moves when the
  // user explicitly drives it (View / Explore / sidebar / country click), all via FlyTo.

  // ---- focus: View / Explore / sidebar-click flies + zooms to a location ---
  // Countries use the bundled polygon; for a region/city we resolve its boundary
  // on demand (even before it's added) so View zooms right into it.
  // Fly/zoom to a location without highlighting it — used when drilling in, where
  // the parent shouldn't be solid-filled (its sub-regions are the focus instead).
  const zoomTo = async (loc: Location) => {
    if (isCountry(loc)) {
      const b = boundsForGeoName(geoNameByCriteria.get(loc.criteria_id));
      if (b) setFlyTo({ bounds: b });
      return;
    }
    let geom = boundaries[loc.criteria_id];
    if (!(loc.criteria_id in boundaries)) {
      requestedRef.current.add(loc.criteria_id);
      setViewLoadingId(loc.criteria_id);
      try {
        geom = await fetchLocationBoundary(businessId, loc.canonical_name || loc.name, loc.criteria_id);
        if (mountedRef.current) setBoundaries((b) => ({ ...b, [loc.criteria_id]: geom ?? null }));
      } finally {
        if (mountedRef.current) setViewLoadingId((id) => (id === loc.criteria_id ? null : id));
      }
    }
    if (geom) {
      const gb = L.geoJSON(featureOf(geom)).getBounds();
      if (gb.isValid()) {
        setFlyTo({ bounds: gb });
        return;
      }
    }
    const cb = boundsForGeoName(countryGeoNameByCC.get(loc.country_code?.toUpperCase() ?? ''));
    if (cb) setFlyTo({ bounds: cb });
  };
  // View / sidebar-click: highlight the location (solid) and zoom to it.
  const focusLocation = (loc: Location) => {
    setFocused(loc);
    return zoomTo(loc);
  };
  // View is a toggle: clicking the location that's already shown clears the
  // selection (un-highlights it + deactivates the View button).
  const toggleView = (loc: Location) => {
    if (focused?.criteria_id === loc.criteria_id) {
      setFocused(null);
    } else {
      void focusLocation(loc);
    }
  };
  // Clicking a saved radius opens it for editing: fly to it and load it into the
  // draft popover (radius editable, "Save changes"), keyed so save updates in place.
  const focusRadius = (r: RadiusFormValue) => {
    // #5497: edit in the unit this radius was saved in, so the input shows mi for a mi radius.
    setRadiusUnit(r.unit ?? 'km');
    setFlyTo({ bounds: L.latLng(r.lat, r.lng).toBounds(Math.max(1, r.radiusKm) * 2400) });
    setProximityDraft({
      lat: r.lat,
      lng: r.lng,
      address: r.formattedAddress,
      radiusKm: r.radiusKm,
      countryCode: r.countryCode,
      locating: false,
      editKey: `${r.lat}:${r.lng}`,
    });
  };
  // Picking a search result seeds a radius draft at that address (rather than just
  // flying past it) so the map popover reflects the search and the radius is ready
  // to adjust before adding (#5359). Clearing the query closes the results dropdown
  // so the draft popover is visible underneath.
  const viewAddress = (r: GeocodeResultRead) => {
    setProximityDraft({
      lat: r.lat,
      lng: r.lng,
      address: r.formatted_address,
      radiusKm: DEFAULT_RADIUS_KM,
      countryCode: r.country_code,
      locating: false,
    });
    setQuery('');
    setFlyTo({ bounds: L.latLng(r.lat, r.lng).toBounds(DEFAULT_RADIUS_KM * 2400) });
  };

  // Drill into a region (country -> its counties, county -> its cities). Pure
  // navigation: extends the breadcrumb trail and zooms, never adds (#5359).
  const exploreLocation = (loc: Location) => {
    setPath((p) => {
      const top = p[p.length - 1];
      // Drilling one level deeper from the current context extends the trail;
      // exploring anything else (a fresh country, a Your-locations item) restarts it.
      return top && loc.parent_id === top.criteria_id ? [...p, loc] : [loc];
    });
    setQuery('');
    setCountryPopup(null);
    setFocused(null); // the explored region is the container now — outline its children, don't fill it
    zoomTo(loc);
    // The pointer is usually already over the country when Explore is clicked, so
    // its hover fill is active; re-apply resting styles to clear it immediately.
    geoLayerRef.current?.eachLayer((layer: Layer) => {
      const nm = ((layer as Layer & { feature?: Feature }).feature?.properties as { name?: string } | undefined)?.name;
      if (nm) (layer as Layer & { setStyle?: (s: PathOptions) => void }).setStyle?.(restingStyleRef.current(nm));
    });
  };
  // Jump to the Proximity tab (the way to target finer than the smallest geo-target).
  // When called from a leaf's popover, carry that area over: geocode its centre and
  // seed a radius draft there so the user lands on a ready-to-adjust radius instead
  // of an empty map (#5359).
  const goToProximity = async (loc?: Location) => {
    setTab('proximity');
    setQuery('');
    setPath([]);
    setProximityDraft(null);
    setCountryPopup(null);
    setFocused(null);
    if (!loc) return;
    setSeedingProximity(true);
    try {
      const results = await geocodeAddress(businessId, loc.canonical_name || loc.name, 1);
      const r = results[0];
      if (r && mountedRef.current) {
        setProximityDraft({
          lat: r.lat,
          lng: r.lng,
          address: r.formatted_address,
          radiusKm: DEFAULT_RADIUS_KM,
          countryCode: r.country_code,
          locating: false,
        });
        setFlyTo({ bounds: L.latLng(r.lat, r.lng).toBounds(DEFAULT_RADIUS_KM * 2400) });
      }
    } finally {
      if (mountedRef.current) setSeedingProximity(false);
    }
  };
  // Breadcrumb: jump back to level i (show that level's regions + zoom to it).
  const navigateToCrumb = (i: number) => {
    setPath(path.slice(0, i + 1));
    setQuery('');
    setCountryPopup(null);
    focusLocation(path[i]);
  };
  // Breadcrumb: leave level i and everything below it; frame the new deepest level.
  const leaveCrumb = (i: number) => {
    const next = path.slice(0, i);
    setPath(next);
    setQuery('');
    setCountryPopup(null);
    const top = next[next.length - 1];
    if (top) focusLocation(top);
  };
  // Map click on a region: zoom to it and open its popover (Explore + Add). The
  // country base layer passes a geojson name; resolve it to our country row.
  openCountryPopupRef.current = (geoName, lat, lng) => {
    // Already exploring inside this country? Clicks belong to its sub-regions, not
    // a fresh country popover — ignore the base-layer hit (#5359).
    if (geoName === exploredCountryGeoNameRef.current) return;
    const cid = criteriaByGeoName.get(geoName);
    const c = cid != null ? countries?.find((x) => x.criteria_id === cid) : undefined;
    if (c) {
      setCountryPopup({ loc: c, lat, lng });
      focusLocation(c);
    }
  };

  // Show the results dropdown: Proximity needs a query; Locations also shows the
  // region browse whenever a country is being explored.
  const showResults = tab === 'proximity' ? query.trim().length > 0 : context !== null || query.trim().length > 0;
  // Show the Radius tab at business level (not catalogOnly) OR in the campaign wizard
  // when radius candidates are supplied (#5444). Plain catalogOnly (campaign Targeting
  // geo-add) keeps it hidden.
  const showRadiusTab = !catalogOnly || radiusCandidates != null;
  // #5498: when exploring a place, its sub-areas are a select-all checklist — a header
  // "Select all" checkbox plus a per-row checkbox. Multi-select only (single-select
  // campaigns target one location).
  const exploreRegions = context && !singleSelect ? (regions ?? []) : [];
  const exploreAddable = exploreRegions.filter((r) => !selectedIds.has(r.criteria_id));
  const exploreIds = new Set(exploreRegions.map((r) => r.criteria_id));
  const allExploreAdded = exploreRegions.length > 0 && exploreAddable.length === 0;
  const someExploreAdded = exploreRegions.some((r) => selectedIds.has(r.criteria_id));
  const toggleSelectAllExplore = () =>
    allExploreAdded
      ? onChange(selected.filter((s) => !exploreIds.has(s.criteria_id) || !!lockedCriteriaIds?.has(s.criteria_id)))
      : onChange([...selected, ...exploreAddable]);

  return (
    <div className="flex h-full min-h-0">
      <div
        className={`flex flex-shrink-0 flex-col border-r border-gray-200 ${catalogOnly ? 'w-[300px]' : 'w-[340px]'}`}
      >
        {/* sub-tabs: Locations (catalog geo) vs Radius (address radius). */}
        {showRadiusTab && (
          <div className="flex gap-1 border-b border-gray-200 px-3.5">
            {(['locations', 'proximity'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTab(t);
                  setQuery('');
                  setPath([]);
                  setProximityDraft(null);
                  setCountryPopup(null);
                  setFocused(null);
                }}
                className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none ${
                  tab === t ? 'border-[#290BD9] text-[#290BD9]' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'proximity' ? 'Radius' : 'Locations'}
              </button>
            ))}
          </div>
        )}

        {/* Search bar doubles as the navigation path; results drop into a panel
            anchored below it. The explored country shows as a removable pill;
            backspace on an empty field (or the pill ✕) navigates out. */}
        <div className="p-3.5" ref={searchBarRef}>
          <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 focus-within:border-[#290BD9]">
            <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
            {/* Breadcrumb trail: a pill per explored level. Click a pill to jump
                back to that level; its ✕ leaves that level (and everything below). */}
            {path.map((loc, i) => (
              <span
                key={loc.criteria_id}
                className="flex flex-shrink-0 items-center gap-1 rounded-md bg-[#EDE9FE] py-0.5 pl-2 pr-1 text-xs font-semibold text-[#290BD9]"
              >
                <button type="button" onClick={() => navigateToCrumb(i)} className="hover:underline">
                  {formatLocationName(loc.name)}
                </button>
                <button
                  type="button"
                  onClick={() => leaveCrumb(i)}
                  aria-label={`Leave ${loc.name}`}
                  // #5501: keep the full 32px tap target but pull the negative margin
                  // back enough that its layout footprint matches the input line, so the
                  // search row doesn't grow taller when a breadcrumb pill appears.
                  className="-my-2 flex h-8 w-8 items-center justify-center text-[#290BD9]/70 transition-colors hover:text-[#290BD9]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && query === '' && context) leaveCrumb(path.length - 1);
              }}
              placeholder={
                context
                  ? `Filter ${context.name}…`
                  : tab === 'proximity'
                    ? 'Search an address…'
                    : 'Search a country, region, or city…'
              }
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Below the search bar: Your locations (always), with the search results
            overlaying it full-height + full-width when searching. */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="border-t border-gray-200" />
          <YourLocations
            tab={tab}
            selected={selected}
            countries={countries ?? []}
            radii={radii}
            candidates={candidates}
            radiusCandidates={radiusCandidates}
            singleSelect={singleSelect}
            lockedCriteriaIds={lockedCriteriaIds}
            onToggle={toggleCatalog}
            onToggleRadius={toggleRadius}
            onRemove={removeCatalog}
            onRemoveRadius={(i) => onRadiiChange(radii.filter((_, idx) => idx !== i))}
            onExplore={exploreLocation}
            onFocus={toggleView}
            onFocusRadius={focusRadius}
            onSelectAll={selectAllCatalog}
            onSelectAllRadii={selectAllRadii}
            focusedId={focused?.criteria_id ?? null}
          />

          {showResults && (
            <div ref={resultsRef} className="absolute left-3 top-1 z-[1000] flex max-h-[calc(100%-0.5rem)] w-[420px] max-w-[80vw] flex-col overflow-y-auto rounded-lg border border-gray-200 bg-white pb-1.5 shadow-lg">
              <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-1.5 pt-1 pb-0.5">
                {exploreRegions.length > 0 ? (
                  <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-xs font-semibold text-[#290BD9] hover:bg-[#EDE9FE]">
                    <input
                      type="checkbox"
                      ref={(el) => {
                        if (el) el.indeterminate = someExploreAdded && !allExploreAdded;
                      }}
                      checked={allExploreAdded}
                      onChange={toggleSelectAllExplore}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-[#290BD9] focus:ring-[#290BD9]"
                    />
                    Select all ({exploreRegions.length - exploreAddable.length}/{exploreRegions.length})
                  </label>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={dismissResults}
                  aria-label="Close results"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {tab === 'proximity' ? (
                <AddressResults
                  query={query}
                  results={geoResults}
                  loading={geoFetching}
                  radii={radii}
                  onView={viewAddress}
                  onAdd={addAddrNow}
                  onRemove={removeAddrNow}
                />
              ) : context ? (
                <RegionList
                  regions={regions ?? []}
                  loading={regionsFetching}
                  selectedIds={selectedIds}
                  excludedCriteriaIds={excludedCriteriaIds}
                  focusedId={focused?.criteria_id ?? null}
                  loadingId={viewLoadingId}
                  onView={toggleView}
                  onAdd={addCatalogNow}
                  onRemove={removeCatalogNow}
                  onHover={setHovered}
                  onExplore={exploreLocation}
                />
              ) : (
                <SearchResults
                  query={query}
                  results={searchResults ?? []}
                  loading={searchFetching}
                  selectedIds={selectedIds}
                  excludedCriteriaIds={excludedCriteriaIds}
                  focusedId={focused?.criteria_id ?? null}
                  loadingId={viewLoadingId}
                  onView={toggleView}
                  onAdd={addCatalogNow}
                  onRemove={removeCatalogNow}
                  onHover={setHovered}
                  onExplore={exploreLocation}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="relative min-w-0 flex-1">
        {(resolvingBoundaries || seedingProximity) && (
          <div className="pointer-events-none absolute left-1/2 top-3 z-[1000] flex -translate-x-1/2 items-center gap-2 rounded-full border border-gray-200 bg-white/95 px-3 py-1 text-xs font-medium text-gray-600 shadow-md">
            <Spinner size="sm" className="text-[#290BD9]" />
            {seedingProximity ? 'Locating…' : 'Locating boundaries…'}
          </div>
        )}
        <MapContainer center={[20, 0]} zoom={2} worldCopyJump scrollWheelZoom style={{ height: '100%', width: '100%' }}>
          <MapResizeFix />
          <MapLibreTiles />
          {geo && (
            <GeoJSON
              ref={geoLayerRef as React.Ref<LeafletGeoJSON>}
              data={geo}
              style={() => COUNTRY_STYLE}
              onEachFeature={onEachCountry}
            />
          )}
          {countryPopup && (
            <Popup
              position={[countryPopup.lat, countryPopup.lng]}
              eventHandlers={{ remove: () => setCountryPopup(null) }}
            >
              <div className="w-[180px]">
                <div className="text-sm font-semibold text-gray-900">{countryPopup.loc.name}</div>
                <div className="mb-2 text-xs capitalize text-gray-500">
                  {countryPopup.loc.target_type?.toLowerCase() || 'country'}
                </div>
                {excludedCriteriaIds?.has(countryPopup.loc.criteria_id) && (
                  <div className="mb-2">
                    <ExcludedBadge />
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  {isExplorable(countryPopup.loc) && (
                    <button
                      type="button"
                      onClick={() => exploreLocation(countryPopup.loc)}
                      className="w-full rounded-md border border-[#C4B5FD] bg-[#EDE9FE] px-3 py-1 text-xs font-bold text-[#290BD9] hover:bg-[#ddd6fe]"
                    >
                      Explore
                    </button>
                  )}
                  {selectedIds.has(countryPopup.loc.criteria_id) ? (
                    <button
                      type="button"
                      disabled={lockedCriteriaIds?.has(countryPopup.loc.criteria_id)}
                      title={
                        lockedCriteriaIds?.has(countryPopup.loc.criteria_id)
                          ? 'Targeted by a campaign — remove it from the campaign first'
                          : undefined
                      }
                      onClick={() => {
                        removeCatalog(countryPopup.loc.criteria_id);
                        setCountryPopup(null);
                      }}
                      className="w-full rounded-md border border-[#FCA5A5] bg-[#FEE2E2] px-3 py-1 text-xs font-semibold text-[#B91C1C] hover:bg-[#FDD6D6] disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:hover:bg-gray-100"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        addCatalogNow(countryPopup.loc);
                        setCountryPopup(null);
                      }}
                      className="w-full rounded-md bg-[#290BD9] px-3 py-1 text-xs font-semibold text-white hover:bg-[#2309C5]"
                    >
                      Add
                    </button>
                  )}
                  {!isExplorable(countryPopup.loc) && (
                    <p className="mt-0.5 text-[11px] leading-snug text-gray-400">
                      Most specific area available here.
                      {!catalogOnly && (
                        <>
                          {' '}
                          Add it, or use a{' '}
                          <button
                            type="button"
                            onClick={() => goToProximity(countryPopup.loc)}
                            className="font-semibold text-[#290BD9] hover:underline"
                          >
                            Radius
                          </button>{' '}
                          target for a custom area.
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </Popup>
          )}
          {/* Frame the area currently being explored (the context/parent) so you
              keep spatial context — including at a leaf you can't drill into. */}
          {tab === 'locations' &&
            context &&
            !isCountry(context) &&
            boundaries[context.criteria_id] && (
              <GeoJSON
                key={`context-${context.criteria_id}`}
                data={featureOf(boundaries[context.criteria_id] as Geometry)}
                style={() => CONTEXT_AREA}
                interactive={false}
              />
            )}
          {/* While exploring a country, outline its regions (counties) as their
              boundaries stream in. Skips any region already shown filled as
              added/focused below to avoid a double layer (#5359). */}
          {tab === 'locations' &&
            context &&
            (regions ?? []).map((r) => {
              if (
                isCountry(r) ||
                !isArea(r) ||
                selectedIds.has(r.criteria_id) ||
                focused?.criteria_id === r.criteria_id ||
                hovered?.criteria_id === r.criteria_id
              )
                return null;
              const geom = boundaries[r.criteria_id];
              if (!geom) return null;
              return (
                <GeoJSON
                  key={`outline-${r.criteria_id}`}
                  data={featureOf(geom)}
                  style={() => REGION_OUTLINE}
                  eventHandlers={{
                    // Click the county itself (not the country beneath it): zoom in
                    // and open its popover (Explore deeper / Add).
                    click: (e) => {
                      setCountryPopup({ loc: r, lat: e.latlng.lat, lng: e.latlng.lng });
                      focusLocation(r);
                    },
                  }}
                />
              );
            })}
          {/* Boundary overlay for each added + focused catalog location, resolved
              on demand from its canonical name (geo-target = source of truth).
              Catalog overlays belong to the Locations tab only (#5359). */}
          {tab === 'locations' &&
            overlayLocations.map((s) => {
              const geom = boundaries[s.criteria_id];
              if (!geom) return null;
              return (
                <GeoJSON
                  key={`boundary-${s.criteria_id}`}
                  data={featureOf(geom)}
                  style={() => regionSelectedStyle}
                  interactive={false}
                />
              );
            })}
          {/* Radius (proximity) overlays belong to the Proximity tab only (#5359). */}
          {tab === 'proximity' &&
            radii.map((r, i) =>
              `${r.lat}:${r.lng}` === proximityDraft?.editKey ? null : (
                <Circle
                  // #5496: key on the radius too, so changing it cleanly replaces the
                  // circle instead of leaving the old-sized one behind.
                  key={`radius-${i}-${r.lat}-${r.lng}-${r.radiusKm}`}
                  center={[r.lat, r.lng]}
                  radius={Math.max(1, r.radiusKm) * 1000}
                  pathOptions={{ color: '#290BD9', weight: 1.5 }}
                />
              ),
            )}
          {tab === 'proximity' &&
            radii.map((r, i) =>
              `${r.lat}:${r.lng}` === proximityDraft?.editKey ? null : (
                <Marker
                  key={`radius-pin-${i}-${r.lat}-${r.lng}`}
                  position={[r.lat, r.lng]}
                  icon={PIN_ICON}
                  // #5499: clicking a saved pin opens that radius (its address + editable
                  // radius), instead of the map-click handler dropping a new draft pin.
                  eventHandlers={{ click: () => focusRadius(r) }}
                />
              ),
            )}
          {/* Proximity tab: click the map to drop a draft radius pin (#2). Suspended while a
              draft popover is open so a click on its "Add radius" button can't leak through to
              the map and drop a second pin at a random spot. */}
          <MapClickHandler
            active={tab === 'proximity' && !proximityDraft}
            onPick={pickProximity}
            suppressRef={suppressMapClickRef}
          />
          {proximityDraft && draftCenter && (
            <>
              <Circle
                center={draftCenter}
                radius={Math.max(1, proximityDraft.radiusKm) * 1000}
                pathOptions={{ color: '#290BD9', weight: 1.5, dashArray: '4' }}
              />
              <Marker position={draftCenter} icon={PIN_ICON} />
              <Popup
                position={draftCenter}
                eventHandlers={{ remove: () => setProximityDraft(null) }}
              >
                <div className="w-[200px]">
                  <div className="mb-2 text-sm font-semibold text-gray-900">
                    {proximityDraft.locating ? (
                      <span className="flex items-center gap-1.5 text-gray-500">
                        <Spinner size="sm" /> Locating address…
                      </span>
                    ) : (
                      proximityDraft.address
                    )}
                  </div>
                  <div className="mb-2 flex items-center gap-1.5">
                    <input
                      type="number"
                      min={radiusInUnit(RADIUS_MIN_KM, radiusUnit)}
                      max={radiusInUnit(RADIUS_MAX_KM, radiusUnit)}
                      value={radiusInUnit(proximityDraft.radiusKm, radiusUnit)}
                      onChange={(e) => {
                        const v = e.target.valueAsNumber;
                        if (!Number.isNaN(v))
                          setProximityDraft((d) => (d ? { ...d, radiusKm: clampKm(unitToKm(v, radiusUnit)) } : d));
                      }}
                      className="w-14 rounded-md border border-gray-200 px-2 py-1 text-sm text-gray-900 focus:border-[#290BD9] focus:outline-none"
                    />
                    <span className="text-xs text-gray-500">radius</span>
                    {/* #5497: km / mi unit switch — converts the displayed value; storage stays km. */}
                    <div className="ml-auto flex overflow-hidden rounded-md border border-gray-200 text-xs font-medium">
                      {(['km', 'mi'] as const).map((u) => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setRadiusUnit(u)}
                          className={`px-2 py-1 transition-colors ${
                            radiusUnit === u ? 'bg-[#290BD9] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!canConfirmRadiusDraft(proximityDraft)}
                    onClick={() => {
                      const d = proximityDraft;
                      if (d.editKey) {
                        // Editing a saved radius: update it in place (same spot, new radius km).
                        onRadiiChange(
                          radii.map((x) =>
                            `${x.lat}:${x.lng}` === d.editKey
                              ? { ...x, radiusKm: d.radiusKm, countryCode: d.countryCode, formattedAddress: d.address, unit: radiusUnit }
                              : x,
                          ),
                        );
                      } else {
                        onRadiiChange([
                          ...radii,
                          {
                            lat: d.lat,
                            lng: d.lng,
                            radiusKm: d.radiusKm,
                            countryCode: d.countryCode,
                            formattedAddress: d.address,
                            unit: radiusUnit,
                          },
                        ]);
                      }
                      setProximityDraft(null);
                      setQuery('');
                      // Swallow the leaked click that would otherwise reach the now-active
                      // map handler and drop a phantom pin (#5435).
                      suppressMapClickRef.current = true;
                      window.setTimeout(() => {
                        suppressMapClickRef.current = false;
                      }, 250);
                    }}
                    className="w-full rounded-md bg-[#290BD9] px-3 py-1 text-xs font-semibold text-white hover:bg-[#2309C5] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#290BD9]"
                  >
                    {proximityDraft.editKey
                      ? 'Save changes'
                      : proximityDraft.countryCode
                        ? 'Add radius'
                        : 'Resolving location…'}
                  </button>
                </div>
              </Popup>
            </>
          )}
          <FlyTo target={flyTo} />
        </MapContainer>
      </div>
    </div>
  );
}

// #1: result-row actions are uniform icon-over-label buttons (an icon with a small
// caption beneath, like the sidebar nav). Two ghost buttons (Explore, View) + Add
// (ghost, fills cobalt on hover); the added state toggles to a remove control. The
// fixed width keeps the row a tidy toolbar and stops the Added/Remove swap jittering.
const ACTION_BTN =
  'flex w-14 flex-shrink-0 flex-col items-center justify-center gap-0.5 rounded-md py-1.5 transition-colors';
const ACTION_LABEL = 'text-[11px] font-medium leading-none';

// #5466: marks a search result / popover whose location is already excluded on the campaign.
function ExcludedBadge() {
  return (
    <span className="flex-shrink-0 rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-semibold text-[#A16207]">
      Excluded
    </span>
  );
}
function ExploreBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Explore sub-areas"
      className={`${ACTION_BTN} text-[#290BD9] hover:bg-[#EDE9FE]`}
    >
      <Search className="h-4 w-4" />
      <span className={ACTION_LABEL}>Explore</span>
    </button>
  );
}
function ViewBtn({ onClick, active, loading }: { onClick: () => void; active?: boolean; loading?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Show on map"
      aria-pressed={active}
      aria-busy={loading}
      // Active = the location currently shown on the map (only one at a time).
      // While its boundary is resolving, show a spinner (not the active fill) so a
      // first-time View reads as loading, not done-but-broken.
      className={`${ACTION_BTN} ${active && !loading ? 'bg-[#EDE9FE] text-[#290BD9]' : 'text-gray-700 hover:bg-gray-100'}`}
    >
      {loading ? <Spinner size="sm" /> : <Eye className="h-4 w-4" />}
      <span className={ACTION_LABEL}>{loading ? 'Loading…' : 'View'}</span>
    </button>
  );
}
function AddBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add"
      // Ghost at rest like its neighbours; fills cobalt on hover to signal the add action.
      className={`${ACTION_BTN} text-[#290BD9] hover:bg-[#290BD9] hover:text-white`}
    >
      <Plus className="h-4 w-4" />
      <span className={ACTION_LABEL}>Add</span>
    </button>
  );
}
function RemoveBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Remove"
      className={`group ${ACTION_BTN} text-[#047857] hover:bg-[#FEE2E2] hover:text-[#B91C1C]`}
    >
      <Check className="h-4 w-4 group-hover:hidden" />
      <Trash2 className="hidden h-4 w-4 group-hover:block" />
      <span className={`${ACTION_LABEL} group-hover:hidden`}>Added</span>
      <span className={`hidden ${ACTION_LABEL} group-hover:block`}>Remove</span>
    </button>
  );
}

function ResultHint({ text, loading }: { text: string; loading?: boolean }) {
  return (
    <p className="flex items-center gap-2 px-3 py-3 text-sm text-gray-400">
      {loading && <Spinner size="sm" className="flex-shrink-0 text-[#290BD9]" />}
      {text}
    </p>
  );
}

function AddressResults({
  query,
  results,
  loading,
  radii,
  onView,
  onAdd,
  onRemove,
}: {
  query: string;
  results: GeocodeResultRead[];
  loading: boolean;
  radii: RadiusFormValue[];
  onView: (result: GeocodeResultRead) => void;
  onAdd: (result: GeocodeResultRead) => void;
  onRemove: (result: GeocodeResultRead) => void;
}) {
  if (query.trim().length < 3) return <ResultHint text="Type at least 3 characters to search." />;
  if (loading && results.length === 0) return <ResultHint text="Searching…" loading />;
  if (results.length === 0) return <ResultHint text="No matches." />;
  return (
    <>
      {results.map((r) => {
        const added = radii.some((x) => x.lat === r.lat && x.lng === r.lng);
        return (
          <div key={r.place_id} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50">
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <span className="flex h-5 flex-shrink-0 items-center">
                <Target className="h-3.5 w-3.5 text-[#290BD9]" />
              </span>
              <button
                type="button"
                onClick={() => onView(r)}
                title="Show on map"
                className="min-w-0 flex-1 break-words text-left text-sm leading-snug text-gray-900 hover:text-[#290BD9]"
              >
                {r.formatted_address}
              </button>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              <ViewBtn onClick={() => onView(r)} />
              {added ? <RemoveBtn onClick={() => onRemove(r)} /> : <AddBtn onClick={() => onAdd(r)} />}
            </div>
          </div>
        );
      })}
    </>
  );
}

function SearchResults({
  query,
  results,
  loading,
  selectedIds,
  excludedCriteriaIds,
  focusedId,
  loadingId,
  onView,
  onAdd,
  onRemove,
  onHover,
  onExplore,
}: {
  query: string;
  results: Location[];
  loading: boolean;
  selectedIds: Set<number>;
  excludedCriteriaIds?: Set<number>;
  focusedId: number | null;
  loadingId: number | null;
  onView: (loc: Location) => void;
  onAdd: (loc: Location) => void;
  onRemove: (loc: Location) => void;
  onHover: (loc: Location | null) => void;
  onExplore: (c: Location) => void;
}) {
  if (query.trim().length < 3) return <ResultHint text="Type at least 3 characters to search." />;
  if (loading && results.length === 0) return <ResultHint text="Searching…" loading />;
  if (results.length === 0) return <ResultHint text="No matches." />;
  return (
    <>
      {[...results].sort(byTypeThenName).map((r) => {
        const added = selectedIds.has(r.criteria_id);
        const country = isCountry(r);
        const isExcluded = excludedCriteriaIds?.has(r.criteria_id) ?? false;
        return (
          <div
            key={r.criteria_id}
            className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50"
            onMouseEnter={() => onHover(r)}
            onMouseLeave={() => onHover(null)}
          >
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <span className="flex h-5 flex-shrink-0 items-center">
                <MapPin className={`h-3.5 w-3.5 ${country ? 'text-[#290BD9]' : 'text-[#2563eb]'}`} />
              </span>
              <button
                type="button"
                onClick={() => onView(r)}
                title="Show on map"
                className="group min-w-0 flex-1 text-left"
              >
                <span className="block break-words text-sm text-gray-900 group-hover:text-[#290BD9]">{formatLocationName(r.name)}</span>
                <span className="block break-words text-xs leading-snug text-gray-400">{formatLocationName(r.canonical_name)}</span>
              </button>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {isExcluded && <ExcludedBadge />}
              {isExplorable(r) && <ExploreBtn onClick={() => onExplore(r)} />}
              <ViewBtn onClick={() => onView(r)} active={focusedId === r.criteria_id} loading={loadingId === r.criteria_id} />
              {added ? <RemoveBtn onClick={() => onRemove(r)} /> : <AddBtn onClick={() => onAdd(r)} />}
            </div>
          </div>
        );
      })}
    </>
  );
}

function RegionList({
  regions,
  loading,
  selectedIds,
  excludedCriteriaIds,
  focusedId,
  loadingId,
  onView,
  onAdd,
  onRemove,
  onHover,
  onExplore,
}: {
  regions: Location[];
  loading: boolean;
  selectedIds: Set<number>;
  excludedCriteriaIds?: Set<number>;
  focusedId: number | null;
  loadingId: number | null;
  onView: (loc: Location) => void;
  onAdd: (loc: Location) => void;
  onRemove: (loc: Location) => void;
  onHover: (loc: Location | null) => void;
  onExplore: (loc: Location) => void;
}) {
  if (loading && regions.length === 0) return <ResultHint text="Searching…" loading />;
  if (regions.length === 0) return <ResultHint text="No regions found." />;
  return (
    <>
      {[...regions].sort(byTypeThenName).map((r) => {
        const added = selectedIds.has(r.criteria_id);
        const isExcluded = excludedCriteriaIds?.has(r.criteria_id) ?? false;
        return (
          <div
            key={r.criteria_id}
            className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50"
            onMouseEnter={() => onHover(r)}
            onMouseLeave={() => onHover(null)}
          >
            {/* #5498: checkbox to add/remove this sub-area; the header has Select all. */}
            <input
              type="checkbox"
              checked={added}
              onChange={() => (added ? onRemove(r) : onAdd(r))}
              aria-label={`Select ${formatLocationName(r.name)}`}
              className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 text-[#290BD9] focus:ring-[#290BD9]"
            />
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <span className="flex h-5 flex-shrink-0 items-center">
                <MapPin className="h-3.5 w-3.5 text-[#2563eb]" />
              </span>
              <button
                type="button"
                onClick={() => onView(r)}
                title="Show on map"
                className="min-w-0 flex-1 break-words text-left text-sm leading-snug text-gray-900 hover:text-[#290BD9]"
              >
                {formatLocationName(r.name)}
              </button>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {isExcluded && <ExcludedBadge />}
              {isExplorable(r) && <ExploreBtn onClick={() => onExplore(r)} />}
              <ViewBtn onClick={() => onView(r)} active={focusedId === r.criteria_id} loading={loadingId === r.criteria_id} />
            </div>
          </div>
        );
      })}
    </>
  );
}

/** Always-visible "Your locations" section: counter + the saved items for the
 * active tab — catalog locations on the Locations tab, radius targets on Radius. */
function YourLocations({
  tab,
  selected,
  countries,
  radii,
  focusedId,
  candidates,
  radiusCandidates,
  singleSelect,
  lockedCriteriaIds,
  onToggle,
  onToggleRadius,
  onRemove,
  onRemoveRadius,
  onExplore,
  onFocus,
  onFocusRadius,
  onSelectAll,
  onSelectAllRadii,
}: {
  tab: 'locations' | 'proximity';
  selected: Location[];
  countries: Location[];
  radii: RadiusFormValue[];
  focusedId: number | null;
  /** When set (campaign wizard), render a checklist: these are the checkable pool
   * (the business's saved locations) that can be applied to the campaign. */
  candidates?: Location[];
  /** When set (campaign wizard), the Radius tab renders a checklist of these
   * (the business's saved radius locations) to apply to the campaign (#5444). */
  radiusCandidates?: RadiusFormValue[];
  singleSelect?: boolean;
  /** Campaign-locked criteria ids — their Remove button is greyed (#4816). */
  lockedCriteriaIds?: Set<number>;
  onToggle?: (loc: Location) => void;
  onToggleRadius?: (r: RadiusFormValue) => void;
  onRemove: (id: number) => void;
  onRemoveRadius: (index: number) => void;
  onExplore: (c: Location) => void;
  onFocus: (s: Location) => void;
  onFocusRadius: (r: RadiusFormValue) => void;
  /** Wizard checklists only: bulk check/clear the "Apply to this campaign" pool. */
  onSelectAll?: (select: boolean) => void;
  onSelectAllRadii?: (select: boolean) => void;
}) {
  const showLocations = tab === 'locations';
  const showRadii = tab === 'proximity';
  const visibleSelected = showLocations ? selected : [];
  // Wizard checklist mode: business locations (candidates) are checked to apply to the
  // campaign and can't be removed (only unchecked); locations added here are
  // campaign-unique and removable.
  const checklist = candidates != null && showLocations;
  const radiusChecklist = radiusCandidates != null && showRadii;
  const selectedIds = new Set(selected.map((s) => s.criteria_id));
  const candidateIds = new Set((candidates ?? []).map((c) => c.criteria_id));
  const pool = checklist
    ? [...new Map([...(candidates ?? []), ...selected].map((l) => [l.criteria_id, l])).values()].sort(byTypeThenName)
    : [];
  // Radius checklist pool: business candidates ∪ campaign radii, deduped by coords
  // (radii override, so a checked one shows the campaign's km). Checked = in `radii`.
  const radiusKey = (r: RadiusFormValue) => `${r.lat}:${r.lng}`;
  const radiiKeys = new Set(radii.map(radiusKey));
  const radiusCandidateKeys = new Set((radiusCandidates ?? []).map(radiusKey));
  const radiusPool = radiusChecklist
    ? [...new Map([...(radiusCandidates ?? []), ...radii].map((r) => [radiusKey(r), r])).values()]
    : [];
  const total = checklist || showLocations ? selected.length : radii.length;
  const isEmpty = checklist ? pool.length === 0 : radiusChecklist ? radiusPool.length === 0 : total === 0;
  // Whether every item in the checklist pool is already applied — drives the
  // Select all / Clear toggle label and action.
  const allChecked = radiusChecklist
    ? radiusPool.length > 0 && radiusPool.every((r) => radiiKeys.has(radiusKey(r)))
    : checklist
      ? pool.length > 0 && pool.every((s) => selectedIds.has(s.criteria_id))
      : false;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-shrink-0 items-center justify-between px-4 pb-1.5 pt-3">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
          {checklist || radiusChecklist ? 'Apply to this campaign' : showLocations ? 'Your locations' : 'Your radius targets'}{' '}
          ({total})
        </span>
        {(checklist || radiusChecklist) && !singleSelect && !isEmpty && (
          <button
            type="button"
            onClick={() => (radiusChecklist ? onSelectAllRadii?.(!allChecked) : onSelectAll?.(!allChecked))}
            className="text-[11px] font-semibold text-[#290BD9] hover:text-[#2309C5]"
          >
            {allChecked ? 'Clear' : 'Select all'}
          </button>
        )}
      </div>
      {isEmpty ? (
        <p className="px-4 pb-3 text-sm leading-relaxed text-gray-400">
          {showLocations
            ? 'Nothing yet. Search a country, region, or city above to add locations.'
            : 'Nothing yet. Search an address above or click the map to add a radius.'}
        </p>
      ) : checklist ? (
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-1.5 pb-2">
          {pool.map((s) => {
            const checked = selectedIds.has(s.criteria_id);
            const isCandidate = candidateIds.has(s.criteria_id);
            const country = isCountry(s) ? countries.find((c) => c.criteria_id === s.criteria_id) : null;
            return (
              <div
                key={s.criteria_id}
                className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 ${
                  checked ? 'bg-[#EDE9FE]' : 'bg-gray-50'
                } ${focusedId === s.criteria_id ? 'ring-1 ring-inset ring-[#290BD9]' : ''}`}
              >
                <input
                  type={singleSelect ? 'radio' : 'checkbox'}
                  name={singleSelect ? 'campaign-location' : undefined}
                  checked={checked}
                  onChange={() => onToggle?.(s)}
                  aria-label={`Apply ${s.name} to this campaign`}
                  className={`h-4 w-4 flex-shrink-0 cursor-pointer border-gray-300 text-[#290BD9] focus:ring-[#290BD9] ${
                    singleSelect ? '' : 'rounded'
                  }`}
                />
                <MapPin className={`h-3.5 w-3.5 flex-shrink-0 ${isCountry(s) ? 'text-[#290BD9]' : 'text-[#2563eb]'}`} />
                <button
                  type="button"
                  onClick={() => onFocus(s)}
                  title="Show on map"
                  className="min-w-0 flex-1 break-words text-left text-sm font-medium leading-snug text-gray-900 hover:text-[#290BD9]"
                >
                  {formatLocationName(s.canonical_name)}
                </button>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {country && (
                    <button
                      type="button"
                      onClick={() => onExplore(country)}
                      aria-label="Explore sub-areas"
                      className={`${ACTION_BTN} text-[#290BD9] hover:bg-[#ddd6fe]`}
                    >
                      <Search className="h-4 w-4" />
                      <span className={ACTION_LABEL}>Explore</span>
                    </button>
                  )}
                  {!isCandidate && !singleSelect && (
                    <button
                      type="button"
                      onClick={() => onRemove(s.criteria_id)}
                      aria-label={`Remove ${s.name}`}
                      className={`${ACTION_BTN} text-gray-700 hover:bg-[#ddd6fe] hover:text-[#B91C1C]`}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className={ACTION_LABEL}>Remove</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : radiusChecklist ? (
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-1.5 pb-2">
          {radiusPool.map((r) => {
            const key = radiusKey(r);
            const checked = radiiKeys.has(key);
            const isCandidate = radiusCandidateKeys.has(key);
            return (
              <div
                key={key}
                className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 ${checked ? 'bg-[#EDE9FE]' : 'bg-gray-50'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleRadius?.(r)}
                  aria-label={`Apply radius ${r.formattedAddress} to this campaign`}
                  className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 text-[#290BD9] focus:ring-[#290BD9]"
                />
                <Target className="h-3.5 w-3.5 flex-shrink-0 text-[#290BD9]" />
                <button
                  type="button"
                  onClick={() => onFocusRadius(r)}
                  title="Show on map"
                  className="flex min-w-0 flex-1 flex-col text-left"
                >
                  <span className="break-words text-sm font-medium leading-snug text-gray-900 hover:text-[#290BD9]">
                    {r.formattedAddress}
                  </span>
                  <span className="text-xs text-gray-400">{radiusInUnit(r.radiusKm, r.unit ?? 'km')} {r.unit ?? 'km'} radius</span>
                </button>
                {!isCandidate && (
                  <button
                    type="button"
                    onClick={() => onRemoveRadius(radii.findIndex((x) => radiusKey(x) === key))}
                    aria-label={`Remove radius ${r.formattedAddress}`}
                    className={`${ACTION_BTN} text-gray-700 hover:bg-[#ddd6fe] hover:text-[#B91C1C]`}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className={ACTION_LABEL}>Remove</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-1.5 pb-2">
          {visibleSelected.map((s) => {
            const country = isCountry(s) ? countries.find((c) => c.criteria_id === s.criteria_id) : null;
            return (
              <div
                key={s.criteria_id}
                className={`flex items-center gap-2 rounded-lg bg-[#EDE9FE] px-3 py-2.5 ${
                  focusedId === s.criteria_id ? 'ring-1 ring-inset ring-[#290BD9]' : ''
                }`}
              >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="flex h-5 flex-shrink-0 items-center">
                    <MapPin className={`h-3.5 w-3.5 ${isCountry(s) ? 'text-[#290BD9]' : 'text-[#2563eb]'}`} />
                  </span>
                  <button
                    type="button"
                    onClick={() => onFocus(s)}
                    title="Show on map"
                    className="min-w-0 flex-1 break-words text-left text-sm font-medium leading-snug text-gray-900 hover:text-[#290BD9]"
                  >
                    {formatLocationName(s.canonical_name)}
                  </button>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {country && (
                    <button
                      type="button"
                      onClick={() => onExplore(country)}
                      aria-label="Explore sub-areas"
                      className={`${ACTION_BTN} text-[#290BD9] hover:bg-[#ddd6fe]`}
                    >
                      <Search className="h-4 w-4" />
                      <span className={ACTION_LABEL}>Explore</span>
                    </button>
                  )}
                  {lockedCriteriaIds?.has(s.criteria_id) ? (
                    <span
                      aria-label={`${s.name} is targeted by a campaign and can't be removed here`}
                      title="Targeted by a campaign — remove it from the campaign first"
                      className={`${ACTION_BTN} cursor-not-allowed text-gray-300`}
                    >
                      <Lock className="h-4 w-4" />
                      <span className={ACTION_LABEL}>Locked</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRemove(s.criteria_id)}
                      aria-label={`Remove ${s.name}`}
                      className={`${ACTION_BTN} text-gray-700 hover:bg-[#ddd6fe] hover:text-[#B91C1C]`}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className={ACTION_LABEL}>Remove</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {showRadii &&
            radii.map((r, i) => (
              <div key={`radius-${i}`} className="flex items-center gap-2 rounded-lg bg-[#EDE9FE] px-3 py-2.5">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="flex h-5 flex-shrink-0 items-center">
                    <Target className="h-3.5 w-3.5 text-[#290BD9]" />
                  </span>
                  <button
                    type="button"
                    onClick={() => onFocusRadius(r)}
                    title="Show on map"
                    className="flex min-w-0 flex-1 flex-col text-left"
                  >
                    <span className="break-words text-sm font-medium leading-snug text-gray-900 hover:text-[#290BD9]">
                      {r.formattedAddress}
                    </span>
                    <span className="text-xs text-gray-400">{radiusInUnit(r.radiusKm, r.unit ?? 'km')} {r.unit ?? 'km'} radius</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveRadius(i)}
                  aria-label={`Remove radius ${r.formattedAddress}`}
                  className={`${ACTION_BTN} text-gray-700 hover:bg-[#ddd6fe] hover:text-[#B91C1C]`}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className={ACTION_LABEL}>Remove</span>
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/** A map mounted inside a just-opened dialog can capture a stale (mid-animation)
 * container size, leaving the tiles offset until something else resizes it (e.g.
 * FitBounds). Re-measure after the dialog settles so the empty / no-selection case
 * — e.g. the Excluded Locations modal, which never fits any bounds — renders
 * aligned. invalidateSize is idempotent, so this is a no-op when already correct. */
function MapResizeFix() {
  const map = useMap();
  useEffect(() => {
    const raf = requestAnimationFrame(() => map.invalidateSize());
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [map]);
  return null;
}

/** Map click → drop a proximity pin (#2). Active only on the Proximity tab. */
function MapClickHandler({
  active,
  onPick,
  suppressRef,
}: {
  active: boolean;
  onPick: (lat: number, lng: number) => void;
  suppressRef: { current: boolean };
}) {
  useMapEvents({
    click(e) {
      // Consume a click that leaked from an in-popover button closing the draft (#5435).
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      if (active) onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/** Animated fly-to when a sidebar item is clicked (#4) — the only thing that moves
 * the map. Clicking one item (View / Explore / country) zooms just to it (#5500). */
function FlyTo({ target }: { target: { bounds: LatLngBounds } | null }) {
  const map = useMap();
  useEffect(() => {
    if (target?.bounds?.isValid()) {
      map.flyToBounds(target.bounds, { padding: [40, 40], maxZoom: 12, duration: 0.6 });
    }
  }, [target, map]);
  return null;
}

export default LocationMapPicker;
